import React from "react";
import { Info } from "lucide-react";
import { useWizard } from "@/context/WizardContext";

/**
 * Banner shown at the top of Step 1 when the wizard was seeded from an
 * Avida Care coordinator deep-link (Prompt 6B). The banner's copy is
 * verbatim from the spec: the RN must actively review the pre-filled
 * fields before moving on.
 *
 * Renders nothing when `banner` is null — Step 1 can safely always
 * mount this component regardless of entry path.
 *
 * Counter: shows how many pre-filled fields are still unverified. The
 * count decrements in real time as the RN edits each seeded field
 * (WizardContext drops the key from prefilledFields on every update).
 */
export default function AvidaSeedBanner({ banner }) {
  const { prefilledFields } = useWizard();
  if (!banner) return null;
  // Exclude agency.* AND physician.* AND insurance.* AND insurance-
  // related client.* keys from the verify-count: those sections are
  // LOCKED for the RN (managed by Avida Care, see Step1Agency
  // `agencyLocked` / `physicianLocked` / `insuranceLocked`). They stay
  // in prefilledFields for completeness but the RN doesn't have an
  // action to take on them, so the banner counts only client.* fields
  // OUTSIDE the insurance block — the demographics the RN actually has
  // to read + confirm + edit if wrong.
  const LOCKED_CLIENT_INSURANCE_KEYS = new Set([
    "client.insurance_type",
    "client.mco_name",
    "client.mco_member_id",
    "client.forwardhealth_member_id",
  ]);
  const verifyKeys = prefilledFields
    ? [...prefilledFields].filter(
        (k) =>
          !k.startsWith("agency.") &&
          !k.startsWith("physician.") &&
          !k.startsWith("insurance.") &&
          !LOCKED_CLIENT_INSURANCE_KEYS.has(k),
      )
    : [];
  const toVerify = verifyKeys.length;
  return (
    <div
      role="status"
      data-testid="avida-seed-banner"
      className="rounded-lg border border-[#B7CADA] bg-[#EEF5FC] p-4 mb-6 flex items-start gap-3"
    >
      <div className="shrink-0 w-8 h-8 rounded-md bg-white border border-[#B7CADA] flex items-center justify-center">
        <Info className="w-4 h-4 text-[#0A3D79]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="text-[14px] font-semibold text-[#0B1E3A]">
            Seeded from Avida Care
          </div>
          <span
            data-testid="avida-seed-banner-count"
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
              toVerify > 0
                ? "bg-[#FFFDEC] text-[#8A6B00] border-[#F0E29A]"
                : "bg-[#E9F5EE] text-[#0E6B3F] border-[#B5D9C3]"
            }`}
          >
            {toVerify > 0
              ? `${toVerify} field${toVerify === 1 ? "" : "s"} to verify`
              : "All fields RN-verified"}
          </span>
        </div>
        <div className="text-[13px] text-[#3A5275] mt-1">
          Review each pre-filled field below and correct anything that has
          changed since intake. Fields with the blue tint came from the
          coordinator; editing one marks it as RN-verified.
        </div>
        {banner.clientName && (
          <div className="text-[12px] text-[#1565B8] font-medium mt-2">
            Client: {banner.clientName}
            {banner.pipelineId && (
              <span className="text-[#3A5275] ml-2 font-normal">
                · pipeline {banner.pipelineId}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
